import {makeFastFuncOfGetI18n} from '@/utils/i18n';
import {ApplyStatusType, DiscountType, ProductsSearchContent, SaleStatusType} from 'supporter';

export type FilteredProductsDataToPrepareForGridData = Pick<ProductsSearchContent,
	'mallNo' | 'mallName' | 'mallProductNo' | 'productName' | 'partnerName' | 'applyStatusType' | 'saleStatusType' |
	'salePrice' | 'optionType' | 'frontDisplayable' | 'onlyAccessUrl' | 'shippingAreaType' | 'deliverable' | 'usesDeliveryDueDate'
	>[];

export interface RecurringPaymentsRegistrationGridDataItem {
	mallNo: number;
	mallName: string;
	productNo: number;
	productName: string;
	partnerName: string;
	applyStatus: ApplyStatusType,
	saleStatusType: SaleStatusType,
	salePrice: number,
	discount?: {
		type: DiscountType;
		value: number;
	},
	discountedPrice: number;
}

export interface CurrentGridDataDiscountInfo extends Pick<RecurringPaymentsRegistrationGridDataItem, 'discount'> {
	isUsedDiscount: boolean;
}

export interface GridProps {
	columns: (
		{
			name: string,
			header: any,
			minWidth: number,
			align: string
			formatter?: (row: any) => any,
		}	|
		{
			name: string,
			width: number,
			header: any,
			align: string,
			formatter?: (row: any) => any,
		}
		)[],
	options: {
		rowHeaders: string[],
		columnOptions: {
			resizable: boolean,
			minWidth: number
		},
		pageOptions: {
			perPage: number;
			page: number;
			totalCount: number;
			centerAlign: boolean;
		},
		haveExcelDownBtn: boolean;
		pageSize: string;
		pageSizes: {
			value: string;
			name: string;
		}[]
	},
	header: {
		height: number
	}
}

const getI18n: (key: string) => string = makeFastFuncOfGetI18n('RECURRINGPAYMENTS_PRODUCT');

export const gridProps = (): GridProps => ({
	header: {
		height: 34,
	},
	columns: [
		{
			header: window.$t('COMMON_MALL'),
			name: 'mallName',
			align: 'center',
			minWidth: 93,
		},
		{
			header: getI18n('PRODUCT_NO'),
			name: 'productNo',
			width: 150,
			align: 'center',
		},
		{
			header: getI18n('PRODUCT_NAME'),
			name: 'productName',
			width: 300,
			align: 'left',
		},
		{
			header: getI18n('PARTNERS'),
			name: 'partnerName',
			width: 160,
			align: 'center',
		},
		{
			header: getI18n('APPROVAL_STATUS'),
			name: 'applyStatus',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => getI18n(row.applyStatus),
		},
		{
			header: getI18n('SALE_STATUS'),
			name: 'saleStatusType',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => getI18n(row.saleStatusType),
		},
		{
			header: getI18n('SELLING_PRICE'),
			name: 'salePrice',
			width: 115,
			align: 'center',
			formatter: ({ row }: any) => row.salePrice.toLocaleString(),
		},
		{
			header: getI18n('DISCOUNT_ON_RECURRING_PAYMENTS_IMMEDIATELY'),
			name: 'discount',
			width: 160,
			align: 'center',
			formatter: ({row}: any) => {
				if (row.discount) {
					switch (row.discount.type) {
						case 'PERCENT' :
							return `${row.discount.value}%`;
						default:
							return row.discount.value.toLocaleString();
					}
				}
				return '-';
			},
		},
		{
			header: getI18n('DISCOUNT_PRICE'),
			name: 'discountedPrice',
			width: 155,
			align: 'center',
			formatter: ({ row }: any) => row.discountedPrice.toLocaleString(),
		},
	],
	options: {
		columnOptions: {
			minWidth: 50,
			resizable: true
		},
		rowHeaders: ['checkbox'],
		pageOptions: {
			perPage: 30,
			page: 1,
			totalCount: 0,
			centerAlign: true
		},
		haveExcelDownBtn: false,
		pageSize: '30',
		pageSizes: [
			{
				value: '10',
				name: 'COMMON_PAGE_SIZE_10'
			},
			{
				value: '30',
				name: 'COMMON_PAGE_SIZE_30'
			},
			{
				value: '50',
				name: 'COMMON_PAGE_SIZE_50'
			},
		],
	}
})

// 정기결제 상품 등록 가능 기준
// '세트 상품이 아니어야 하는' 조건도 존재 -> 따로 확인 해야함 (optionType !== 'MAPPING')
export const STATUS_ALLOWED_TO_REGISTER_RECURRING_PAYMENTS_PRODUCTS = {
	applyStatusType: 'FINISHED', // 승인 완료여야 함
	saleStatusType: 'ON_SALE', // 판매중이어야 함
	frontDisplayable: true, // 전시 가능해야 함
	onlyAccessUrl: false, // 프론트 노출 상태여야 함
	usesDeliveryDueDate: false, // 배송지정일 설정되어있지 않아야 함
	deliverable: true, // 배송 가능해야 함
	shippingAreaType: 'MALL_SHIPPING_AREA', // 쇼핑몰 배송이어야 함
} as const;